import { useQuery } from '@tanstack/react-query'
import { AddOnsService } from '@mc/api'

/**
 * The certificate credit add-on this card sells.
 *
 * The response is nullable: the backend returns null while its kill switch is
 * off or the pricing plan is unseeded (PRCNG-1117). `isUnavailable` folds that
 * together with a failed request, because in both cases there is no honest
 * price and the card must not render — unlike "still fetching", which resolves.
 *
 * `discount` is a raw number, not a formatted percentage — the copy owns the
 * `%`. The backend omits it, along with the saving, where no anchor certificate
 * is priced in the same currency or the add-on saves nothing (PRCNG-1176). The
 * price line has no copy without it, so a missing discount is unavailable too.
 */
export const useCertificateCreditAddOn = ({
  enabled,
}: {
  enabled: boolean
}) => {
  const {
    data: addOn,
    isLoading,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: ['certificateCreditAddOn'],
    queryFn: () => AddOnsService.getCertificateCreditAddOn(),
    staleTime: 1000 * 60 * 2,
    enabled,
  })

  return {
    price: addOn?.monetizedPrice ?? '',
    savings: addOn?.monetizedSavings ?? '',
    currency: addOn?.currency ?? '',
    productId: addOn?.productId ?? 0,
    productType: addOn?.productType ?? '',
    isLoading: enabled && isLoading,
    isUnavailable:
      isError || (isSuccess && (!addOn?.monetizedPrice || !addOn?.discount)),
    monthlyPrice: addOn?.monetizedMonthlyPrice ?? '',
    discount: addOn?.discount,
  }
}
